import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  PhotoCamera,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import vehicleService from '../../services/vehicleService';
import { useMileageAI } from '../../hooks/useMileageAI';

interface MileageEntryProps {
  vehicleId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  currentMileage: number;
}

interface AIAnalysisDisplayProps {
  analyzing: boolean;
  aiMileage: number | null;
  aiConfidence: number | null;
  validationResult: any;
  onAccept: () => void;
  onEdit: () => void;
  t: any;
}

const AIAnalysisDisplay: React.FC<AIAnalysisDisplayProps> = ({
  analyzing,
  aiMileage,
  aiConfidence,
  validationResult,
  onAccept,
  onEdit,
  t
}) => {
  if (analyzing) {
    return (
      <Box display="flex" alignItems="center" gap={1} my={2}>
        <CircularProgress size={20} />
        <Typography>{t('vehicle.mileage.ai.analyzing')}</Typography>
      </Box>
    );
  }

  if (!aiMileage) return null;

  return (
    <Box my={2}>
      <Typography variant="subtitle2" gutterBottom>
        {t('vehicle.mileage.ai.detected')}:
      </Typography>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Typography variant="h6">{aiMileage}</Typography>
        <Chip
          size="small"
          icon={<CheckCircleIcon />}
          label={`${t('vehicle.mileage.ai.confidence')}: ${Math.round(aiConfidence! * 100)}%`}
          color="success"
        />
      </Box>
      
      {validationResult && !validationResult.isValid && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('vehicle.mileage.ai.inconsistent')}</AlertTitle>
          {validationResult.reason}
        </Alert>
      )}

      <Box display="flex" gap={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={onAccept}
          startIcon={<CheckCircleIcon />}
        >
          {t('vehicle.mileage.ai.confirm')}
        </Button>
        <Button
          variant="outlined"
          onClick={onEdit}
          startIcon={<EditIcon />}
        >
          {t('vehicle.mileage.ai.edit')}
        </Button>
      </Box>
    </Box>
  );
};

interface CameraModalProps {
  open: boolean;
  onClose: () => void;
  onPhotoTaken: (file: File) => void;
}

const CameraModal: React.FC<CameraModalProps> = ({ open, onClose, onPhotoTaken }) => {
  const { t } = useTranslation();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    if (open) {
      navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { exact: "environment" }
        }
      })
      .then((stream) => {
        setStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch((err) => {
        console.error('Error accessing camera:', err);
        setError(t('vehicle.mileage.errors.cameraAccess'));
      });
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [open, t]);

  const handleCapture = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'dashboard-photo.jpg', { type: 'image/jpeg' });
            onPhotoTaken(file);
            onClose();
          }
        }, 'image/jpeg', 0.95);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {t('vehicle.mileage.takePhoto')}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain' }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCapture}
          startIcon={<PhotoCamera />}
        >
          {t('vehicle.mileage.capture')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MileageEntry: React.FC<MileageEntryProps> = ({
  vehicleId,
  open,
  onClose,
  onSubmit,
  currentMileage,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [mileage, setMileage] = useState<number>(currentMileage);
  const [notes, setNotes] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    analyzing,
    aiMileage,
    aiConfidence,
    validationResult,
    error: aiError,
    analyzeImage,
    validateMileage,
    reset: resetAI
  } = useMileageAI();

  useEffect(() => {
    if (aiMileage) {
      validateMileage(aiMileage, currentMileage);
    }
  }, [aiMileage, currentMileage, validateMileage]);

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setShowManualInput(false);
      await analyzeImage(file);
    }
  };

  const handleAcceptAiReading = () => {
    if (aiMileage) {
      setMileage(aiMileage);
      setShowManualInput(true);
    }
  };

  const handleEditReading = () => {
    setShowManualInput(true);
  };

  const handleSubmit = async () => {
    try {
      if (!user) return;
      
      setIsSubmitting(true);
      
      if (mileage <= currentMileage) {
        setError(t('vehicle.mileage.errors.invalidMileage'));
        setIsSubmitting(false);
        return;
      }

      let imageUrl = null;
      if (selectedImage) {
        const vehicle = await vehicleService.getVehicle(vehicleId);
        if (!vehicle) {
          throw new Error(t('vehicle.errors.notFound'));
        }
        const document = await vehicleService.uploadDocument(
          vehicleId,
          vehicle.organizationId,
          selectedImage,
          'maintenance'
        );
        imageUrl = document.url;
      }

      // Créer le rapport avec les informations d'IA
      const reportId = await vehicleService.addMileageReport(vehicleId, {
        mileage,
        notes,
        imageUrl,
        reportedBy: user.id,
        aiVerified: !!aiMileage,
        aiConfidence: aiConfidence ?? undefined,
        originalAiReading: aiMileage || undefined,
        validationStatus: validationResult?.isValid ? 'validated' : 'pending',
        validationMessage: validationResult?.reason
      });

      // Mettre à jour le kilométrage actuel du véhicule
      await vehicleService.updateVehicle(vehicleId, {
        currentMileage: mileage,
        lastMileageUpdate: Timestamp.now()
      });

      // Attendre un court délai pour que l'utilisateur voie le succès
      setTimeout(() => {
        onSubmit();
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Error submitting mileage:', err);
      setError(err instanceof Error ? err.message : t('common.error'));
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setMileage(currentMileage);
    setNotes('');
    setSelectedImage(null);
    setPreviewUrl(null);
    setError(null);
    setShowManualInput(false);
    resetAI();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {t('vehicle.mileage.add')}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box my={2}>
          <TextField
            label={t('vehicle.mileage.currentMileage')}
            type="number"
            value={currentMileage}
            disabled
            fullWidth
            margin="normal"
            helperText={t('vehicle.mileage.current')}
          />

          {showManualInput && (
            <>
              <TextField
                label={t('vehicle.mileage.add')}
                type="number"
                value={mileage}
                onChange={(e) => setMileage(Number(e.target.value))}
                fullWidth
                margin="normal"
                error={!!error}
                helperText={error || t('vehicle.mileage.ai.checkValue')}
              />
              <TextField
                label={t('common.notes')}
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                margin="normal"
              />
            </>
          )}
          
          <Box mt={2} display="flex" alignItems="center" gap={2}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="mileage-photo"
              type="file"
              onChange={handleImageSelect}
            />
            <label htmlFor="mileage-photo">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCamera />}
                disabled={analyzing}
                sx={{ display: 'inline-flex' }}
              >
                {analyzing ? t('vehicle.mileage.ai.analyzing') : t('vehicle.details.buttons.uploadPhoto')}
              </Button>
            </label>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PhotoCamera />}
              disabled={analyzing}
              onClick={() => setShowCamera(true)}
            >
              {t('vehicle.details.buttons.takePhoto')}
            </Button>
            <CameraModal
              open={showCamera}
              onClose={() => setShowCamera(false)}
              onPhotoTaken={async (file) => {
                setSelectedImage(file);
                setPreviewUrl(URL.createObjectURL(file));
                setShowManualInput(false);
                await analyzeImage(file);
              }}
            />
          </Box>

          {aiError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {aiError}
            </Alert>
          )}

          <AIAnalysisDisplay
            analyzing={analyzing}
            aiMileage={aiMileage}
            aiConfidence={aiConfidence}
            validationResult={validationResult}
            onAccept={handleAcceptAiReading}
            onEdit={handleEditReading}
            t={t}
          />

          {previewUrl && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                {t('vehicle.details.buttons.viewDashboardPhoto')}:
              </Typography>
              <Box
                component="img"
                src={previewUrl}
                alt="Dashboard preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                }}
              />
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setSelectedImage(null);
                  setPreviewUrl(null);
                  resetAI();
                  setShowManualInput(false);
                }}
              >
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      {/* Indicateur de chargement global */}
      {isSubmitting && (
        <Box 
          position="absolute" 
          top={0} 
          left={0} 
          width="100%" 
          zIndex={9999}
        >
          <LinearProgress />
        </Box>
      )}
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !showManualInput || mileage <= currentMileage}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MileageEntry;
